<script lang="ts">
    import type {MenuItem} from 'shared/components/kp-main-menu/types';
    import type {UIcons} from 'shared/ui-widgets/uicons/types';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {exists, uuid} from 'shared/utils/custom-utils';
    import KpMainMenuNavItemSubMenu from 'shared/components/kp-main-menu/menu-nav/KpMainMenuNavItemSubMenu.svelte';
    import {getMainMenuContext} from 'shared/components/kp-main-menu/main-menu-context';
    import {onDestroy, tick} from 'svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';

    export let menuItem: MenuItem;
    export let icon: UIcons | null = null;
    export let additionalClasses = '';
    export let dataQaPrefix = 'main-menu-item';

    const context = getMainMenuContext();
    const itemUuid = uuid();

    let anchorElement: HTMLElement;
    let subMenuOpen = false;
    let hoveringOver = false;
    let subMenuClickOpen = false;
    let isStuckToTop = false;
    const isStuckToTopUnsubscribe = context.isStuckToTop.subscribe((currentIsStuckToTop) => isStuckToTop = currentIsStuckToTop);
    $: hasSubMenu = exists(menuItem.submenu);

    onDestroy(() => {
        isStuckToTopUnsubscribe();
    });

    const handleItemClick = async () => {
        if (hasSubMenu) {
            subMenuClickOpen = !subMenuClickOpen;
            return;
        }

        await context.service.performClick(menuItem);
    }

    const handleMouseEnter = () => {
        const touchDevice = matchMedia('(hover: none)').matches;
        if (touchDevice) {
            return;
        }

        hoveringOver = true;
        // The actual hiding/showing of the submenu is delayed by one tick,
        // because the hoveringOver property first changes the z-index
        // of the submenu hovering over, and then animates.
        // Without this, the changed z-index would not work.
        tick().then(() => subMenuOpen = true);
    }

    const handleMouseLeave = () => {
        hoveringOver = false;
        tick().then(() => subMenuOpen = false);
    }

    const handleSubMenuClickOutsideOrItemClick = () => {
        subMenuOpen = false;
        subMenuClickOpen = false;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Space') {
            subMenuClickOpen = !subMenuClickOpen;
            event.preventDefault();
            return;
        }

        if (event.key !== 'Tab' || !exists(anchorElement)) {
            return;
        }

        anchorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        });
    }
</script>

<li class="kp-main-menu-nav-item"
    on:mouseenter={handleMouseEnter}
    on:mouseleave={handleMouseLeave}>

    <a class="link-anchor menu-id-{menuItem.id} menu-uuid-{itemUuid} {additionalClasses}"
       role="button"
       aria-expanded="{subMenuOpen || subMenuClickOpen}"
       aria-controls="kp-main-nav-item-submenu-{itemUuid}"
       data-qa="{dataQaPrefix}-{menuItem.id}"
       href={context.service.getMenuItemAnchorHref(menuItem)}
       bind:this={anchorElement}
       class:link={!hasSubMenu}
       class:menu={hasSubMenu}
       class:menu-click-open={subMenuClickOpen}
       class:not-stuck-to-top={!isStuckToTop}
       on:click={handleItemClick}
       on:keydown={handleKeyDown}>

        {#if icon}
            <UIcon icon="{icon}"/>
        {/if}

        {menuItem.text}

        {#if menuItem.userActionRequired}
            <KpChipTag additionalClasses="warning-chip" chipSize="xs" chipStyle="danger-new">
                <UIcon icon="exclamation" color="white"/>
            </KpChipTag>
        {/if}

        {#if hasSubMenu}
            <div class="open-indicator-container" class:open={subMenuOpen || subMenuClickOpen}>
                <UIcon icon="angle-small-down"/>
            </div>
        {/if}
    </a>

    {#if hasSubMenu && (subMenuOpen || subMenuClickOpen) && exists(anchorElement)}
        <KpMainMenuNavItemSubMenu {anchorElement}
                                  {hoveringOver}
                                  {itemUuid}
                                  {dataQaPrefix}
                                  subMenu="{menuItem.submenu}"
                                  on:click-outside={handleSubMenuClickOutsideOrItemClick}
                                  on:item-click={handleSubMenuClickOutsideOrItemClick}/>
    {/if}
</li>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .kp-main-menu-nav-item {
        position: relative;
        height: 100%;
        display: flex;

        &:hover .link-anchor {
            background-color: var(--main-menu-highlight-background-color);
            color: var(--main-menu-highlight-text-color)
        }

        .link-anchor {
            color: inherit;
            text-decoration: none;
            position: relative;
            height: 100%;
            display: flex;
            align-items: center;
            gap: @padding-xs-horizontal;
            padding: 0 @padding-large-horizontal;
            transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;

            &.not-stuck-to-top {
                border-top-left-radius: @border-radius-base;
                border-top-right-radius: @border-radius-base;
            }

            &.menu-click-open {
                background-color: var(--main-menu-highlight-background-color);
            }

            &:focus-visible {
                outline-offset: -6px !important;
                box-shadow: none !important;
            }
        }

        &.menu .open-indicator-container {
            transition: transform 0.2s ease-in-out;

            &.open {
                transform: rotate(-180deg);
            }
        }
    }
</style>